<template>
  <BaseTableRow
    :options="options"
    zebra
    :routerLink="routerLink"
    @optionClick="optionClick($event, template)"
  >
    <BaseTableCell :width="50">{{ template.name }}</BaseTableCell>
    <BaseTableCell :width="36">
      <div>
        {{ $t(isContract ? 'forms.contract' : 'forms.consultation').toLowerCase() }}
        <BaseText
          v-if="isArchived"
          inline
          italic
        >
          ({{ $t('form_templates.archived').toLowerCase() }})
        </BaseText>
      </div>
    </BaseTableCell>
    <BaseTableCell :width="14">{{ template.submissionsCount }}</BaseTableCell>
  </BaseTableRow>
</template>

<script
  lang="ts"
  setup
>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import axios from '@/axios';
import BaseTableCell from '@/components/_deprecated/BaseTableCell.vue';
import BaseTableRow from '@/components/_deprecated/BaseTableRow.vue';
import { triggerFileDownload } from '@/helpers/download';
import { useFormTemplatesStore } from '@/stores/form-templates';
import { useUserStore } from '@/stores/user';
import type { FormTemplate } from '@/types';

const { user } = useUserStore();
const router = useRouter();
const { archive, delete: deleteFormTemplate } = useFormTemplatesStore();

const props = defineProps<{
  template: FormTemplate;
}>();

const routerLink = computed(() => ({
  name: 'admin-forms-form',
  params: {
    id: props.template.id,
  },
}));

const isArchived = computed(() => !!props.template.archivedAt);
const isContract = computed(() => props.template.formType === 'contract');
const options = computed(() => {
  const editable =
    !props.template.corona &&
    (!isContract.value || !props.template.submissionsCount);

  const options = [
    {
      name: 'edit',
      show: editable,
    },
    {
      name: 'export',
      show: user?.owner,
    },
    {
      name: 'archive',
      show: !isArchived.value,
    },
    {
      name: 'unarchive',
      show: isArchived.value,
    },
    {
      name: 'delete',
      show: editable,
    },
  ];

  return options.filter((option) => option.show).map((option) => option.name);
});

async function downloadFormTemplateExport() {
  const { data, headers } = await axios.get<Blob>(
    `/form_template_exports/${props.template.id}`,
    { responseType: 'blob' },
  );
  const disposition = headers['content-disposition'] as string | undefined;
  const fileNameMatch = disposition?.match(/filename="([^"]+)"/);
  const fileName =
    fileNameMatch?.[1] ?? `form_template_${props.template.id}.xls`;

  triggerFileDownload(
    data,
    headers['content-type'] || 'application/vnd.ms-excel',
    fileName,
  );
}

const optionClick = (action: string) => {
  switch (action) {
    case 'edit':
      router.push(routerLink.value);
      break;
    case 'archive':
    case 'unarchive':
      archive(props.template.id);
      break;
    case 'export':
      void downloadFormTemplateExport();
      break;
    case 'delete':
      deleteFormTemplate(props.template.id);
      break;
  }
};
</script>
